export function format(first?: string, middle?: string, last?: string): string {
    return (first || "") + (middle ? ` ${middle}` : "") + (last ? ` ${last}` : "");
}

/**
 * Parses chat messages for links in the format [Text|URL] and converts them to HTML anchor tags
 *
 * @param message - The message text to parse
 * @returns The message with links converted to HTML anchor tags
 *
 * @example
 * // Returns: 'Check our <a href="https://example.com" target="_blank" rel="noopener noreferrer">privacy policy</a>'
 * parseLinks('Check our [privacy policy|https://example.com]')
 */
export function parseLinks(message: string): string {
    if (!message) {
        return "";
    }

    // Regex pattern to match [Text|URL] format
    // Also handles the case where there might be a pipe in the text like [Text | Label|URL]
    const linkPattern = /\[(.*?)\|(https?:\/\/[^\]\s]+)\]/g;

    return message.replace(linkPattern, (_match, text, url) => {
        // Make sure text and URL are trimmed of any whitespace
        const trimmedText = text.trim();
        const trimmedUrl = url.trim();

        // Return an anchor tag with the text and URL
        return `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${trimmedText}</a>`;
    });
}

/**
 * Debug logging utility that only logs when the component has debug=true
 *
 * @param component - The component instance with a debug property
 * @param message - The message to log
 * @param data - Optional data to log along with the message
 *
 * @example
 * // In a component with @Prop() debug = false;
 * debug.log(this, "Component loaded"); // Nothing will be logged
 *
 * // In a component with @Prop() debug = true;
 * debug.log(this, "Component loaded"); // Will log: "[ComponentName] Component loaded"
 * debug.log(this, "Data received", { id: 123 }); // Will log: "[ComponentName] Data received", { id: 123 }
 */
export const debug = {
    log: (component: { debug?: boolean; constructor?: { name?: string } }, message: string, ...data: unknown[]) => {
        if (component?.debug) {
            const componentName = component.constructor?.name || "UnknownComponent";
            if (data.length > 0) {
                console.log(`[${componentName}] ${message}`, ...data);
            } else {
                console.log(`[${componentName}] ${message}`);
            }
        }
    }
};
